import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { LogIn, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

export function LoginPage() {
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const user = await login(identifier, password)
      // Si debe cambiar contraseña, redirigir a esa página
      if (user?.mustChangePassword) {
        navigate('/change-password', { replace: true })
      } else {
        navigate(from, { replace: true })
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Error al iniciar sesión'
      const isDeactivated = errorMessage.toLowerCase().includes('desactivad')
      setError(isDeactivated
        ? 'Tu cuenta ha sido desactivada. Contacta con un administrador para más información.'
        : errorMessage
      )
      toast.error(isDeactivated
        ? 'Cuenta desactivada'
        : errorMessage, {
        duration: 5000,
        description: isDeactivated
          ? 'Tu cuenta ha sido desactivada. Contacta con un administrador para más información.'
          : 'Por favor, verifica tus credenciales e intenta de nuevo'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center gradient-viscofan dark:bg-dark-bg py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-secondary-500/20 dark:bg-secondary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-primary-400/20 dark:bg-primary-400/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-md w-full relative">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-5 sm:p-8 backdrop-blur-xl border border-transparent dark:border-slate-700">
          {/* Logo */}
          <div className="text-center mb-6 sm:mb-8">
            <img
              src="/logo/ES_Viscofan_50_Logo.png"
              alt="Viscofan"
              className="h-16 sm:h-20 w-auto mx-auto mb-3 sm:mb-4"
            />
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Sistema de Gestión de Licencias
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {error && (
              <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 animate-slide-in">
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-error-500 flex-shrink-0" />
                <p className="text-xs sm:text-sm text-error-700 dark:text-error-400">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="identifier" className="label text-sm">
                DNI / Email
              </label>
              <input
                id="identifier"
                type="text"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="input text-base py-2.5 sm:py-3"
                placeholder="12345678A o admin@viscofan.es"
                autoComplete="username"
              />
            </div>

            <div>
              <label htmlFor="password" className="label text-sm">
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input text-base py-2.5 sm:py-3"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-xs sm:text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary flex items-center justify-center gap-2 py-3 text-sm sm:text-base"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              ) : (
                <>
                  <LogIn className="h-4 w-4 sm:h-5 sm:w-5" />
                  Iniciar Sesión
                </>
              )}
            </button>
          </form>

          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 dark:border-dark-border">
            <p className="text-center text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mb-2">
              Acceso:
            </p>
            <div className="space-y-1 text-center text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
              <p><strong>Empleados:</strong> Introduce tu DNI</p>
              <p><strong>Administradores:</strong> Introduce tu email</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs sm:text-sm text-white/60 dark:text-gray-400 mt-6 sm:mt-8 px-4">
          © {new Date().getFullYear()} Viscofan España S.L.U. Todos los derechos reservados.
        </p>
      </div>
    </div>
  )
}
